import { useEffect } from 'react';

import { useTranslation } from 'react-i18next';
import { LuArrowLeft } from 'react-icons/lu';

import { ActivitySection } from './ActivitySection';
import { ContactSection } from './ContactSection';
import { CrmLinkageSection } from './CrmLinkageSection';
import { FilesSection } from './FilesSection';
import { LinkedCasesSection } from './LinkedCasesSection';
import { StatusSection } from './StatusSection';
import { TagsSection } from './TagsSection';
import Spinner from '~/components/icons/Spinner';
import { useChatContextLoanRequestId } from '~/features/whatsapp/hooks/useChatContextLoanRequestId';
import { useChats } from '~/features/whatsapp/hooks/useChats';
import { leaveActiveChat, popChatPanelView } from '~/features/whatsapp/stores/chatPanelStore';
import { deriveChatDisplayName } from '~/features/whatsapp/utils/deriveChatDisplayName';
import { getInitials } from '~/features/whatsapp/utils/getInitials';
import { isPartnerChat } from '~/features/whatsapp/utils/isPartnerChat';

type Props = {
    chatId: string;
};

export const UserInfoPanel = ({ chatId }: Props) => {
    const { t } = useTranslation();
    const { data: chats } = useChats();
    const chat = chats?.find((c) => c.id === chatId);
    const primaryLoanRequestId = useChatContextLoanRequestId(chatId);

    // Bail to list if chat drops out of getChats (unlink / ACL change).
    useEffect(() => {
        if (chats != null && !chat) {
            leaveActiveChat();
        }
    }, [chats, chat]);

    if (chats == null) {
        return (
            <div className="flex h-full items-center justify-center">
                <Spinner />
            </div>
        );
    }

    if (!chat) {
        return null;
    }

    const displayName = deriveChatDisplayName(chat);
    // Partner-only chats have no case to link — the linking UI would only offer unrelated leads.
    const isPartnerOnlyChat = isPartnerChat(chat) && chat.loanRequestIds.length === 0;

    return (
        <div className="flex h-full min-h-0 flex-col overflow-y-auto" data-testid="user-info-panel">
            <div className="flex shrink-0 items-center gap-2 border-b border-outline-light px-4 py-3">
                <button
                    type="button"
                    onClick={popChatPanelView}
                    aria-label={t('WHATSAPP_CHAT.CHAT.BACK')}
                    className="p-1 text-midnight"
                >
                    <LuArrowLeft />
                </button>
            </div>
            <div className="flex shrink-0 flex-col items-center gap-3 border-b border-outline-light px-4 py-6">
                <span className="flex size-20 items-center justify-center rounded-full bg-grey-light text-lg font-semibold text-grey">
                    {getInitials(displayName)}
                </span>
                <span className="text-sm font-medium text-midnight">{displayName}</span>
            </div>

            <ContactSection
                displayName={displayName}
                phone={chat.phone ?? chat.customerIdentity}
                loanRequestId={primaryLoanRequestId}
            />
            <StatusSection
                lastInboundAt={chat.lastInboundAt}
                lastMessageAt={chat.lastMessageAt}
                lastMessageDirection={chat.lastMessageDirection}
            />
            {primaryLoanRequestId && <CrmLinkageSection loanRequestId={primaryLoanRequestId} />}
            {!isPartnerOnlyChat && (
                <LinkedCasesSection
                    chatId={chatId}
                    loanRequestIds={chat.loanRequestIds}
                    partnerIds={chat.partnerIds}
                    phone={chat.phone}
                    phoneNumberId={chat.phoneNumberId}
                />
            )}
            <ActivitySection />
            <FilesSection chatId={chatId} />
            <TagsSection />
        </div>
    );
};
